/**
 * The router for the application.
 *
 * File: \src\router\router.ts
 * Created Date: Tuesday, February 13th 2018, 10:09:33
 * Author: Sascha Herrmann
 * -----
 * Last Modified: November 18th 2024, 13:57:58
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2018 Liebherr-Hausgeräte GmbH
 */

import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

const Home = () => import('@/views/Home.vue');

const Dashboard = () => import('@/views/dashboard/Dashboard.vue');
const LoginError = () => import('@/components/login/LoginError.vue');

const MessageEditor = () => import('@/views/messageEditor/MessageEditor.vue');

const HomePage = () => import('@/views/homepage/HomePage.vue');
const UserCenter = () => import('@/views/user/UserCenter.vue');

const SupportCenter = () => import('@/views/support/SupportCenter.vue');
const HelpInfo = () => import('@/views/helpinfo/HelpInfo.vue');
const BusinessStatistics = () => import('@/views/statistics/BusinessStatistics.vue');

import { useLoginStore } from '@/components/login/LoginStore';
import { useUpdateStore } from '@/components/update/ui/UpdateStore';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'Home', component: Home, meta: { publicAccess: true } },
    {
      path: '/homepage',
      name: 'HomePage',
      component: HomePage,
      beforeEnter: (to, _from, next) => {
        const loginStore = useLoginStore();

        if (!loginStore.isAuthenticated) {
          loginStore.checkStoredLogin();
        }
        const updateStore = useUpdateStore();
        updateStore.routeChanged(to);
        next();
      },
      children: [
        // Dashboard related routes
        { path: '/dashboard', name: 'Dashboard', component: Dashboard, meta: { updateDate: 'SOCKET' } },
        {
          path: '/News',
          name: 'News',
          component: MessageEditor,
          props: true,
          beforeEnter: checkInternalAccess
        },
        {
          path: '/UserCenter',
          name: 'UserCenter',
          component: UserCenter,
          props: false,
          beforeEnter: checkInternalAccess
        },
        {
          path: '/Statistics',
          name: 'Statistics',
          component: BusinessStatistics,
          props: false,
          beforeEnter: checkInternalAccess
        },
        {
          path: '/SupportCenter',
          name: 'SupportCenter',
          component: SupportCenter,
          props: false,
          beforeEnter: (_to, _from, next) => {
            const loginStore = useLoginStore();

            if (loginStore.isSupportCenterAccessAllowed) {
              next();
            } else {
              next({ name: 'Dashboard' });
            }
          }
        },
        {
          path: '/HelpInfo',
          name: 'HelpInfo',
          component: HelpInfo,
          props: false,
          beforeEnter: checkInternalAccess
        }
      ]
    },

    // Login related routes
    {
      path: '/loginStart',
      name: 'LoginStart',
      redirect: '',
      beforeEnter: (to, from, next) => {
        next(false);
        const loginStore = useLoginStore();
        loginStore.loginStart();
      }
    },
    {
      path: '/logoutStart',
      name: 'LogoutStart',
      redirect: '',
      beforeEnter: (to, from, next) => {
        next(false);
        const loginStore = useLoginStore();
        loginStore.logoutStart();
      }
    },
    {
      path: '/loginResult',
      name: 'LoginResult',
      redirect: '',
      meta: { publicAccess: true },
      beforeEnter: (to, from, next) => {
        next(false);
        const loginStore = useLoginStore();
        loginStore.loginResult();
      }
    },
    { path: '/error/:reason', name: 'LoginError', component: LoginError, meta: { publicAccess: true }, props: true }
  ]
});

// If the user is not authenticated, then he can only access Home (and LoginResult
// as the result of the login) and the external applications call.
// See https://router.vuejs.org/guide/advanced/navigation-guards.html
router.beforeEach((to, _from, next) => {
  // If user is authenticated, then just proceed
  const loginStore = useLoginStore();

  if (loginStore.isAuthenticated) {
    const updateStore = useUpdateStore();
    updateStore.routeChanged(to);
  }

  next();
});

// Checks if the user is allowed to access routes flagged as internal.
function checkInternalAccess(_to, _from, next): void {
  const loginStore = useLoginStore();
  if (loginStore.isInternalAccessAllowed) {
    next();
  } else {
    next({ name: 'Dashboard' });
  }
}

export default router;
